import { useState, useCallback } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { api } from '@/services/api'
import { Card, Table, Pagination, Input, Select } from '@/components/ui'
import { DEFAULT_PAGE_SIZE } from '@spoofio/shared'
import { formatAuditTimestamp } from '@/utils/format-date'
import type { AdminOrganizationResponse } from '@spoofio/shared/types'

type AuditLogEntry = {
  id: string
  action: string
  resourceType: string
  resourceId: string | null
  userEmail: string
  organizationId: string | null
  metadata: Record<string, unknown> | null
  createdAt: string
}

const ACTION_OPTIONS = [
  { value: '', label: 'All Actions' },
  { value: 'admin.org.update', label: 'Org Update' },
  { value: 'admin.org.suspend', label: 'Org Suspend' },
  { value: 'admin.org.reactivate', label: 'Org Reactivate' },
  { value: 'admin.org.extend_trial', label: 'Org Extend Trial' },
  { value: 'admin.org.cancel', label: 'Org Cancel' },
  { value: 'admin.user.update', label: 'User Update' },
  { value: 'admin.user.password_reset', label: 'User Password Reset' },
  { value: 'admin.canary.update', label: 'Canary Update' },
  { value: 'admin.canary.delete', label: 'Canary Delete' },
  { value: 'admin.detection.resend_alert', label: 'Detection Resend Alert' },
  { value: 'admin.allowlist.create', label: 'Allowlist Create' },
  { value: 'admin.allowlist.update', label: 'Allowlist Update' },
  { value: 'admin.allowlist.delete', label: 'Allowlist Delete' },
  { value: 'admin.impersonation.start', label: 'Impersonation Start' },
  { value: 'admin.impersonation.end', label: 'Impersonation End' },
]

export function AdminAuditLogPage() {
  const [searchParams, setSearchParams] = useSearchParams()

  const actionParam = searchParams.get('action') ?? ''
  const emailParam = searchParams.get('userEmail') ?? ''
  const orgParam = searchParams.get('orgId') ?? ''
  const dateFromParam = searchParams.get('dateFrom') ?? ''
  const dateToParam = searchParams.get('dateTo') ?? ''
  const pageParam = parseInt(searchParams.get('page') ?? '1', 10)

  const [actionFilter, setActionFilter] = useState(actionParam)
  const [emailFilter, setEmailFilter] = useState(emailParam)
  const [orgFilter, setOrgFilter] = useState(orgParam)
  const [dateFrom, setDateFrom] = useState(dateFromParam)
  const [dateTo, setDateTo] = useState(dateToParam)
  const [currentPage, setCurrentPage] = useState(pageParam)

  const syncParams = useCallback(
    (params: Record<string, string>) => {
      const sp = new URLSearchParams()
      for (const [k, v] of Object.entries(params)) {
        if (v) sp.set(k, v)
      }
      setSearchParams(sp)
    },
    [setSearchParams]
  )

  const updateFilter = useCallback(
    (key: string, value: string) => {
      const state: Record<string, string> = {
        action: actionFilter,
        userEmail: emailFilter,
        orgId: orgFilter,
        dateFrom,
        dateTo,
        page: '1',
        [key]: value,
      }
      setCurrentPage(1)
      syncParams(state)
    },
    [actionFilter, emailFilter, orgFilter, dateFrom, dateTo, syncParams]
  )

  const offset = (currentPage - 1) * DEFAULT_PAGE_SIZE

  const { data: orgsData } = useQuery({
    queryKey: ['admin-organizations-dropdown'],
    queryFn: () => api.adminListOrganizations({ limit: 200 }),
    staleTime: 60_000,
  })

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-audit-logs', actionFilter, emailFilter, orgFilter, dateFrom, dateTo, offset],
    queryFn: () =>
      api.adminListAuditLogs({
        action: actionFilter || undefined,
        userEmail: emailFilter || undefined,
        orgId: orgFilter || undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        limit: DEFAULT_PAGE_SIZE,
        offset,
      }),
    staleTime: 30_000,
  })

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
    syncParams({
      action: actionFilter,
      userEmail: emailFilter,
      orgId: orgFilter,
      dateFrom,
      dateTo,
      page: String(page),
    })
  }

  const orgOptions = [
    { value: '', label: 'All Organizations' },
    ...(orgsData?.data.map((o: AdminOrganizationResponse) => ({
      value: o.id,
      label: o.name,
    })) ?? []),
  ]

  const columns = [
    {
      key: 'createdAt',
      header: 'Timestamp',
      render: (e: AuditLogEntry) => formatAuditTimestamp(e.createdAt),
    },
    {
      key: 'userEmail',
      header: 'Actor',
      render: (e: AuditLogEntry) => (
        <span className="text-sm">{e.userEmail}</span>
      ),
    },
    {
      key: 'action',
      header: 'Action',
      render: (e: AuditLogEntry) => (
        <span className="font-mono text-xs">{e.action}</span>
      ),
    },
    {
      key: 'resourceType',
      header: 'Resource Type',
      render: (e: AuditLogEntry) => (
        <span className="capitalize text-sm">{e.resourceType}</span>
      ),
    },
    {
      key: 'resourceId',
      header: 'Resource ID',
      render: (e: AuditLogEntry) => (
        <span className="font-mono text-xs text-gray-500">
          {e.resourceId ? `${e.resourceId.slice(0, 12)}...` : '-'}
        </span>
      ),
    },
    {
      key: 'organizationId',
      header: 'Organization',
      render: (e: AuditLogEntry) => {
        if (!e.organizationId) return <span className="text-gray-400">-</span>
        const org = orgsData?.data.find(
          (o: AdminOrganizationResponse) => o.id === e.organizationId
        )
        return <span className="text-sm">{org?.name ?? e.organizationId.slice(0, 8)}</span>
      },
    },
  ]

  const totalPages = data ? Math.ceil(data.total / DEFAULT_PAGE_SIZE) : 1

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">Audit Log</h1>

      <Card>
        <div className="flex flex-wrap gap-4 mb-4">
          <Select
            label="Action"
            name="action"
            value={actionFilter}
            onChange={(e) => {
              setActionFilter(e.target.value)
              updateFilter('action', e.target.value)
            }}
            options={ACTION_OPTIONS}
            className="w-56"
          />
          <Input
            label="Actor Email"
            name="userEmail"
            placeholder="Filter by email..."
            value={emailFilter}
            onChange={(e) => {
              setEmailFilter(e.target.value)
              updateFilter('userEmail', e.target.value)
            }}
            className="w-56"
          />
          <Select
            label="Organization"
            name="orgId"
            value={orgFilter}
            onChange={(e) => {
              setOrgFilter(e.target.value)
              updateFilter('orgId', e.target.value)
            }}
            options={orgOptions}
            className="w-48"
          />
          <Input
            label="Date From"
            name="dateFrom"
            type="date"
            value={dateFrom}
            onChange={(e) => {
              setDateFrom(e.target.value)
              updateFilter('dateFrom', e.target.value)
            }}
            className="w-40"
          />
          <Input
            label="Date To"
            name="dateTo"
            type="date"
            value={dateTo}
            onChange={(e) => {
              setDateTo(e.target.value)
              updateFilter('dateTo', e.target.value)
            }}
            className="w-40"
          />
        </div>

        {error ? (
          <div className="text-center py-8">
            <p className="text-danger-600">Failed to load audit logs</p>
          </div>
        ) : (
          <>
            <Table
              columns={columns}
              data={data?.data ?? []}
              keyExtractor={(e) => e.id}
              isLoading={isLoading}
              emptyMessage="No audit log entries found"
            />
            {data && data.total > DEFAULT_PAGE_SIZE && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={data.total}
                itemsPerPage={DEFAULT_PAGE_SIZE}
                onPageChange={handlePageChange}
              />
            )}
          </>
        )}
      </Card>
    </div>
  )
}

